import {useMemo} from "react";

import {WorkspaceTour, type WorkspaceTourStep} from "./WorkspaceTour";

interface FinancialModelTourProps {
    open: boolean;
    activeTab: string;
    visibleTabs: string[];
    onClose: () => void;
    onTabChange: (tab: string) => void;
}

const TOUR_STEPS: WorkspaceTourStep[] = [
    {
        id: "financial-overview",
        selector: '[data-tour="dashboard-tab-bar"]',
        title: "This page is the working model and workflow layer",
        body: "The dashboard gives the headline read. These tabs are where planning, weekly cash, operations, data workflow, and setup happen.",
        badge: "Overview",
        placement: "bottom",
    },
    {
        id: "modeling-workbench",
        tab: "modeling_workbench",
        selector: '[data-tour="dashboard-tab-trigger-modeling_workbench"]',
        title: "Modeling Workbench is the bottom-up planning surface",
        body: "Use this tab for revenue and expense modeling, reusable templates, and driver changes that feed the current 3-statement engine.",
        badge: "Planning",
        placement: "bottom",
    },
    {
        id: "forecast-summary",
        tab: "forecast_summary",
        selector: '[data-tour="dashboard-tab-trigger-forecast_summary"]',
        title: "Forecast Summary is the consolidated 3-statement readout",
        body: "Use this tab when you want the current scenario packaged into one forward-looking summary without working inside the modeling surface.",
        badge: "Forecast",
        placement: "bottom",
    },
    {
        id: "cashflow-explorer",
        tab: "cashflow_explorer",
        selector: '[data-tour="dashboard-tab-trigger-cashflow_explorer"]',
        title: "Cash Flow Explorer is the weekly cash engine",
        body: "This is the live 13-week cash forecast. Use it for short-term liquidity, weekly overrides, and tie health rather than monthly summary review.",
        badge: "Cash Flow",
        placement: "bottom",
    },
    {
        id: "ar",
        tab: "ar",
        selector: '[data-tour="dashboard-tab-trigger-ar"]',
        title: "AR is the collections-risk snapshot",
        body: "Use this tab when the cash question is really an accounts receivable question: DSO, overdue mix, customer concentration, and collection trend.",
        badge: "AR",
        placement: "bottom",
    },
    {
        id: "transactions",
        tab: "transactions",
        selector: '[data-tour="dashboard-tab-trigger-transactions"]',
        title: "Transactions is the ledger drilldown surface",
        body: "Go here when a number needs transaction-level proof. It is the clean landing point for provenance follow-through and record-by-record review.",
        badge: "Transactions",
        placement: "bottom",
    },
    {
        id: "knowledge",
        tab: "knowledge",
        selector: '[data-tour="dashboard-tab-trigger-knowledge"]',
        title: "Knowledge controls what external context reaches the model",
        body: "Upload files here, let extraction run, then approve items as context, approve them for forecast use, or preview impact before applying anything.",
        badge: "Knowledge",
        placement: "bottom",
    },
    {
        id: "sandbox",
        tab: "sandbox",
        selector: '[data-tour="dashboard-tab-trigger-sandbox"]',
        title: "Sandbox is for custom one-off analysis",
        body: "Use Sandbox when you want analysis artifacts that combine model data, uploads, and knowledge without forcing the work into a standard statement tab.",
        badge: "Sandbox",
        placement: "bottom",
    },
    {
        id: "ops",
        tab: "ops",
        selector: '[data-tour="dashboard-tab-trigger-ops"]',
        title: "Ops is the model change history",
        body: "This is where active operations, revisions, and agent-versus-human edits are tracked, reviewed, undone, or cleaned up.",
        badge: "Ops",
        placement: "bottom",
    },
    {
        id: "contact-mapping",
        tab: "contact_mapping",
        selector: '[data-tour="dashboard-tab-trigger-contact_mapping"]',
        title: "Contact Mapping classifies customers and counterparties",
        body: "Use AI auto-map, bulk approval, and manual cleanup here so the model understands parent contacts, segments, and business grouping correctly.",
        badge: "Mapping",
        placement: "bottom",
    },
    {
        id: "account-mapping",
        tab: "account_mapping",
        selector: '[data-tour="dashboard-tab-trigger-account_mapping"]',
        title: "Account Mapping sets the statement structure",
        body: "This tab is where account groups and classifications are cleaned up so the statements and forecast logic roll up the way you expect.",
        badge: "Mapping",
        placement: "bottom",
    },
    {
        id: "data-sync",
        tab: "data_sync",
        selector: '[data-tour="dashboard-tab-trigger-data_sync"]',
        title: "Data Sync is the deterministic setup path",
        body: "Use the step rows here to run syncs in order, verify status badges, and rebuild the source data the model depends on before trusting outputs.",
        badge: "Data Sync",
        placement: "bottom",
    },
    {
        id: "agent-panel",
        selector: '[data-tour="agent-panel"]',
        requiresAgentPanel: true,
        title: "The left agent panel ties the workflow together",
        body: "The agent stays context-aware to the workspace and active tab, so you can ask for analysis, scenario help, mapping cleanup, or model changes without leaving the workflow.",
        badge: "Agent Panel",
        placement: "bottom",
    },
];

export function FinancialModelTour({open, activeTab, visibleTabs, onClose, onTabChange}: FinancialModelTourProps) {
    const steps = useMemo(
        () => TOUR_STEPS.filter((step) => !step.tab || visibleTabs.includes(step.tab)),
        [visibleTabs],
    );

    return (
        <WorkspaceTour
            open={open}
            activeTab={activeTab}
            onClose={onClose}
            onTabChange={onTabChange}
            steps={steps}
            dialogLabel="Financial model walkthrough"
        />
    );
}
